// src/components/ViewMessages.tsx
import React, { useEffect, useState } from "react";
import { PlayerRole, type Role } from "../shared/constants";
import { sortByTimestampDescending } from "../shared/helper";
import { API_BASE_URL } from "../utils/api";

interface Message {
  message_id: string;
  timestamp: string;
  turn_number: number;
  sending_player_id: string | null;
  sending_player_name: string | null;
  sending_player_code_name: string | null;
  sending_team_name: string | null;
  sending_player_role: Role | null;
  admin_id?: string | null;
  is_admin_message?: boolean;
  admin_name?: string | null;
  receiving_player_id: string;
  receiving_player_name: string;
  receiving_player_code_name: string;
  receiving_team_name: string;
  receiving_player_role: Role;
  original_message: string;
  final_message: string;
}

interface ViewMessagesProps {
  teamName: string;
  playerRole: Role;
  playerId: string;
  websocket: WebSocket | null;
  onReply?: (targetPlayerId: string) => void; // Callback when reply button is clicked
  gameStatus?: string | null; // Game status for disablement logic
  isEliminated?: boolean; // Whether player is eliminated
  timerEnabled?: boolean; // Whether timer is enabled
  isPaused?: boolean; // Whether timer is paused
  timerSeconds?: number | null; // Current timer seconds
}

export default function ViewMessages({ teamName, playerRole, playerId, websocket, onReply, gameStatus, isEliminated = false, timerEnabled = true, isPaused = false, timerSeconds }: ViewMessagesProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  const fetchMessages = React.useCallback(() => {
    if (!teamName) return;

    console.log("Fetching messages for:", teamName, playerRole);
    fetch(`${API_BASE_URL}/messages/by-player?team_name=${encodeURIComponent(teamName)}&player_role=${playerRole}`)
      .then(res => {
        if (!res.ok) {
          // Handle 404 as empty array (endpoint now returns 200 with empty array, but legacy support)
          if (res.status === 404) {
            return [];
          }
          throw new Error("Failed to fetch messages");
        }
        return res.json();
      })
      .then((data: Message[]) => {
        // Handle both array and non-array responses
        const messagesArray = Array.isArray(data) ? data : [];
        const sorted = sortByTimestampDescending(messagesArray, (msg) => msg.timestamp);
        setMessages(sorted);
        setLoading(false);
        console.log("Messages fetched:", sorted.length);
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      });
  }, [teamName, playerRole]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Listen for WebSocket messages and custom events
  useEffect(() => {
    const handleNewMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const messageData = customEvent.detail;
      console.log("newMessage event received:", messageData);
      
      // Check if the message is relevant to this player
      if (messageData && messageData.message_id) {
        const msg = messageData;
        // Only add if the message is sent to or from this player
        const isRelevant = 
          (msg.sending_player_id === playerId || msg.target_player_id === playerId || msg.receiving_player_id === playerId) ||
          (msg.sending_team_name === teamName || msg.receiving_team_name === teamName);
        
        if (isRelevant) {
          // Check if message already exists (avoid duplicates)
          setMessages(prevMessages => {
            const exists = prevMessages.some(m => m.message_id === msg.message_id);
            if (exists) {
              return prevMessages; // Already have this message
            }
            // Add new message to the list and sort by timestamp
            const newMessages = [...prevMessages, {
              message_id: msg.message_id,
              timestamp: msg.timestamp,
              turn_number: msg.turn_number,
              sending_player_id: msg.sending_player_id || null,
              sending_player_name: msg.sending_player_name || null,
              sending_player_code_name: msg.sending_player_code_name || null,
              sending_team_name: msg.sending_team_name || null,
              sending_player_role: msg.sending_player_role || null,
              admin_id: msg.admin_id || null,
              is_admin_message: msg.is_admin_message || msg.admin_id || !msg.sending_player_id,
              admin_name: msg.admin_name || null,
              receiving_player_id: msg.target_player_id || msg.receiving_player_id,
              receiving_player_name: msg.receiving_player_name || "",
              receiving_player_code_name: msg.receiving_player_code_name || "",
              receiving_team_name: msg.receiving_team_name || "",
              receiving_player_role: msg.receiving_player_role,
              original_message: msg.original_message || "",
              final_message: msg.final_message || msg.original_message || ""
            }];
            return sortByTimestampDescending(newMessages, (m) => m.timestamp);
          });
        }
      } else {
        // Fallback: If message data is incomplete, refetch (shouldn't happen in normal flow)
        console.warn("Incomplete message data in event, refetching messages");
        fetchMessages();
      }
    };

    // Listen for custom events from PlayerDashboard
    window.addEventListener('newMessage', handleNewMessage);
    console.log("Event listener registered for 'newMessage'");

    return () => {
      window.removeEventListener('newMessage', handleNewMessage);
      console.log("Event listener removed for 'newMessage'");
    };
  }, [teamName, playerId, fetchMessages]);

  // Build aligned team → players map to render agencies on one row and spaceships on another
  const buildTeamPlayers = () => {
    const teamOrder: string[] = [];
    const teamToPlayers: Record<string, {
      agency?: { id: string; name: string };
      spaceship?: { id: string; name: string };
    }> = {};

    const consider = (teamName: string | null, playerId: string | null, playerName: string | null, role: Role | null) => {
      if (!teamName || !playerId || !playerName || !role) return;
      if (!teamToPlayers[teamName]) {
        teamToPlayers[teamName] = {};
        teamOrder.push(teamName);
      }
      if (role === PlayerRole.AGENCY) {
        teamToPlayers[teamName].agency = { id: playerId, name: playerName };
      } else if (role === PlayerRole.SPACESHIP) {
        teamToPlayers[teamName].spaceship = { id: playerId, name: playerName };
      }
    };

    messages.forEach(msg => {
      consider(msg.sending_team_name, msg.sending_player_id, msg.sending_player_name, msg.sending_player_role);
      consider(msg.receiving_team_name, msg.receiving_player_id, msg.receiving_player_name, msg.receiving_player_role);
    });

    return { teamOrder, teamToPlayers };
  };

  // Filter messages based on selected player or admin
  const filteredMessages = selectedPlayerId === "admin"
    ? messages.filter(msg => msg.is_admin_message || msg.admin_id || !msg.sending_player_id)
    : selectedPlayerId
      ? messages.filter(msg => 
          msg.sending_player_id === selectedPlayerId || 
          msg.receiving_player_id === selectedPlayerId
        )
      : messages;

  const { teamOrder, teamToPlayers } = buildTeamPlayers();

  return (
    <div style={{ 
      border: "1px solid #dee2e6", 
      padding: "1.5rem", 
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h2 style={{ margin: 0, color: "#212529", fontSize: "1.25rem" }}>Messages</h2>
        
        {!loading && !error && messages.length > 0 && (
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={() => setSelectedPlayerId(null)}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                cursor: "pointer",
                backgroundColor: selectedPlayerId === null ? "#007bff" : "#e9ecef",
                color: selectedPlayerId === null ? "white" : "#212529",
                border: "none",
                borderRadius: "4px"
              }}
            >
              All Messages
            </button>
            
            {/* Admin filter */}
            <button
              onClick={() => setSelectedPlayerId(selectedPlayerId === "admin" ? null : "admin")}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                cursor: "pointer",
                backgroundColor: selectedPlayerId === "admin" ? "#007bff" : "#e9ecef",
                color: selectedPlayerId === "admin" ? "white" : "#212529",
                border: "none",
                borderRadius: "4px"
              }}
            >
              Admin
            </button>
            
            {/* Show only spaceships for agencies, only agencies for spaceships */}
            {playerRole === PlayerRole.AGENCY ? (
              // Agencies see spaceships
              teamOrder.map((team) => {
                const p = teamToPlayers[team]?.spaceship;
                if (!p) return null;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlayerId(selectedPlayerId === p.id ? null : p.id)}
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      backgroundColor: selectedPlayerId === p.id ? "#007bff" : "#e9ecef",
                      color: selectedPlayerId === p.id ? "white" : "#212529",
                      border: "none",
                      borderRadius: "4px"
                    }}
                  >
                    {p.name}
                  </button>
                );
              })
            ) : (
              // Spaceships see agencies
              teamOrder.map((team) => {
                const p = teamToPlayers[team]?.agency;
                if (!p) return null;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlayerId(selectedPlayerId === p.id ? null : p.id)}
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      backgroundColor: selectedPlayerId === p.id ? "#007bff" : "#e9ecef",
                      color: selectedPlayerId === p.id ? "white" : "#212529",
                      border: "none",
                      borderRadius: "4px"
                    }}
                  >
                    {p.name}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      
      {loading && (
        <div style={{ padding: "2rem", textAlign: "center", color: "#6c757d" }}>
          Loading messages...
        </div>
      )}
      
      {error && (
        <div style={{ 
          padding: "1rem", 
          backgroundColor: "#f8d7da", 
          color: "#721c24", 
          borderRadius: "4px",
          border: "1px solid #f5c6cb"
        }}>
          {error}
        </div>
      )}
      
      {!loading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            fontSize: "0.875rem"
          }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Sender</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Receiver</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Turn</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {messages.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    No messages yet.
                  </td>
                </tr>
              ) : selectedPlayerId && filteredMessages.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    {selectedPlayerId === "admin" ? "No admin messages found" : "No messages found for selected player"}
                  </td>
                </tr>
              ) : (
                filteredMessages.map(msg => {
                const isAdminMessage = msg.is_admin_message || msg.admin_id || !msg.sending_player_id;
                
                // Determine if current player is receiver or sender for row coloring
                const isReceiver = msg.receiving_player_id === playerId;
                const isSender = msg.sending_player_id === playerId;
                
                // Set row background color based on message type
                let rowBackgroundColor: string | undefined;
                if (isAdminMessage) {
                  rowBackgroundColor = "#e3f2fd"; // Light blue for admin messages (same as Actions ADMIN)
                } else if (isSender) {
                  rowBackgroundColor = "#fff3cd"; // Pale yellow for messages sent by player (same as Actions PENDING)
                } else if (isReceiver) {
                  rowBackgroundColor = "#f3e5f5"; // Pale purple/lavender for messages sent to player
                }
                
                const rowStyle = {
                  borderBottom: "1px solid #dee2e6",
                  ...(rowBackgroundColor && { backgroundColor: rowBackgroundColor })
                };
                
                const senderName = isAdminMessage
                  ? (msg.admin_name || "Admin")
                  : (msg.sending_player_name || "");

                const receiverName = msg.receiving_player_name || "";

                return (
                  <tr key={msg.message_id} style={rowStyle}>
                    <td style={{ padding: "0.75rem", color: "#212529" }}>
                      {isAdminMessage ? (
                        <strong style={{ color: "#1976d2" }}>{senderName}</strong>
                      ) : (
                        senderName || "Unknown"
                      )}
                    </td>
                    <td style={{ padding: "0.75rem", color: "#212529" }}>{receiverName || "Unknown"}</td>
                    <td style={{ padding: "0.75rem", color: "#212529" }}>{msg.turn_number}</td>
                    <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "300px", wordWrap: "break-word" }}>
                      {playerRole === PlayerRole.SPACESHIP ? msg.final_message : msg.original_message}
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
